import { createStyles, useMantineTheme, Box, type BoxProps } from '@mantine/core';
import React from 'react';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';

import MarketplaceBadge from '~/domains/design-system/badges/MarketplaceBadge/MarketplaceBadge';
import { getContestBanner } from '~/domains/contest/domains/detail/components/ContestHeader/utils/getContestBanner';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';
import type { MyUserContest } from '~/domains/contest/domains/myContests/types/userContest';

import ContestCountdownPill from '../ContestCountdownPill/ContestCountdownPill';
import GuaranteedPlusCountdownPill from '../GuaranteedPlusCountdownPill/GuaranteedPlusCountdownPill';

const useStyles = createStyles((theme) => ({
  bannerBadges: {
    position: 'absolute',
    top: theme.other.spacing._8,
    right: theme.other.spacing._8,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.other.spacing._4,
  },
}));

type ContestCardBannerProps = {
  contest: Contest | MyUserContest;
  isBannerEnabled?: boolean;
  isCountdownPillEnabled?: boolean;
  // The in-flow height (outer box): the vertical space the banner reserves, which pushes the
  // content below it down. Default 140.
  height?: BoxProps['h'];
  // The painted height (inner bleeding box holding the image). Default 140. Lets a caller paint a
  // hero taller or shorter than the in-flow box reserves.
  paintedHeight?: BoxProps['h'];
  // How far the painted box bleeds up and out so it sits flush to the card's outer edge — the
  // caller's border + padding. Default 20. A caller with different insets passes its own.
  bleedPadding?: number;
};

function ContestCardBanner({
  contest,
  isBannerEnabled = false,
  isCountdownPillEnabled = false,
  height = 140,
  paintedHeight = 140,
  bleedPadding = 20,
}: ContestCardBannerProps) {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const { t } = useTranslation('contest');
  if (!isBannerEnabled) return null;
  const isMarketplaceEnabled = !!contest.marketplace_enabled;
  const countdownPill = isCountdownPillEnabled ? (
    <ContestCountdownPill
      targetDate={new Date(contest.start_date)}
      sx={{
        position: 'absolute',
        zIndex: 1,
        left: 8,
        bottom: 8,
      }}
    />
  ) : null;

  return (
    <Box h={height} w="100%" pos="relative">
      <Box
        pos="absolute"
        w={`calc(100% + ${bleedPadding * 2}px)`}
        h={paintedHeight}
        style={{
          margin: `-${bleedPadding}px -${bleedPadding}px 0 -${bleedPadding}px`,
        }}
      >
        <Image
          fill
          style={{ objectFit: 'cover' }}
          alt={contest.name}
          src={getContestBanner({
            contestBannerlUrl: contest?.banner_url,
            sport: contest.league.sport,
          })}
          sizes={`${theme.breakpoints.xl * 2}px`}
          quality={100}
        />
        {countdownPill}
        <div className={classes.bannerBadges}>
          {isMarketplaceEnabled && (
            <MarketplaceBadge label={t('common.contestCard.banner.marketplace')} />
          )}
          <GuaranteedPlusCountdownPill contest={contest} />
        </div>
      </Box>
    </Box>
  );
}

export default ContestCardBanner;
